import clsx from "clsx";
import type { ButtonHTMLAttributes } from "react";
import React from "react";

const variantClasses = {
	primary:
		"bg-Surface-action-primary text-Text-primary focus:bg-Surface-action-primary-hover hover:bg-Surface-action-primary-hover disabled:bg-Primary-200 disabled:text-Text-disabled" as const,
	secondary:
		"bg-Surface-action-secondary hover:bg-Surface-action-secondary-hover focus:bg-Surface-action-secondary-focus text-Text-primary disabled:text-Text-disabled border border-Border-primary" as const,
	danger: "bg-Surface-error hover:bg-red-200 focus:bg-red-200 text-Text-primary disabled:text-Text-disabled" as const,
	outline:
		"border border-Border-primary-light hover:border-Border-primary focus:border-Border-action-neutral-focus text-Text-primary disabled:border-Border-primary disabled:text-Text-disabled" as const,
	neutral:
		"bg-Surface-action-neutral text-Text-primary hover:bg-Surface-action-neutral focus:bg-Surface-action-neutral-focus disabled:text-Text-disabled" as const,
	ghost: "text-Text-primary disabled:text-Text-disabled" as const,
	selected: clsx("bg-Surface-neutral text-white"),
};

const sizeClasses = {
	medium: "h-[40px] leading-sm text-size-base" as const,
	small: "h-[30px] leading-xsm text-size-sm" as const,
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: keyof typeof variantClasses;
	size?: keyof typeof sizeClasses;
	text?: string;
	iconLeft?: React.ReactNode;
	iconRight?: React.ReactNode;
	loading?: boolean;
}

export function Button({
	iconLeft,
	iconRight,
	variant = "primary",
	size = "medium",
	className,
	disabled = false,
	children,
	loading,
	type = "button",
	text,
	...props
}: ButtonProps) {
	return (
		<button
			className={clsx(
				"transition-colors duration-200 flex justify-center items-center px-[12px] py-[15px] rounded-[6px] cursor-pointer font-medium w-[100%]",
				variantClasses[variant],
				sizeClasses[size],
				className
			)}
			disabled={disabled}
			{...props}
			type={type}
		>
			<div className="flex items-center">
				{iconLeft && <span className={clsx("flex items-center", (!!children || !!text) && "mr-2")}>{iconLeft}</span>}

				<span className="flex items-center w-fit relative">
					<div className={clsx("flex items-center gap-2", loading ? "opacity-0" : "opacity-100")}>
						{text ?? ""}
						{children ?? ""}
					</div>
					{loading && (
						<div className="absolute left-0 right-0 bottom-0 top-0 flex items-center justify-center">
							<span
								className="inline-block w-5 h-5 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"
								aria-hidden="true"
							/>
						</div>
					)}
				</span>
				{iconRight}
			</div>
		</button>
	);
}
